// src/components/Universals/ExportDropdown/exportDropdown.component.jsx
import React from "react";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { useDataLayer } from "../../../../Contexts/DataLayer.context.jsx";
import { getEasternDateParts } from "../../../../utils/dateUtils.ts";
import { decodeFBX } from "../../../../utils/functions.ts";

const BOX_WIDTH = 260;
const EXPORT_ROW_LIMIT = 100;
const SECURITY_LEVEL_ONE_EXPORT_ROW_LIMIT = 500;
const EXPORT_LIMIT_LAST_NAME = "Export Limit Reached";

const cleanValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (
            trimmed === "" ||
            trimmed === "-" ||
            trimmed.toLowerCase() === "null" ||
            trimmed === "0000-00-00"
        ) {
            return "";
        }
        return trimmed;
    }
    return value;
};

const joinClean = (...values) => {
    return values
        .map((value) => cleanValue(value))
        .filter((value) => value !== "")
        .join(" ");
};

const getFileTimestamp = () => {
    const now = getEasternDateParts();

    return [
        now.year,
        now.month,
        now.day,
    ].join("-") + `_${now.hour}${now.minute}`;
};

const jerseyValue = (value) => {
    const cleaned = cleanValue(value);
    if (cleaned === "0") return "";
    if (cleaned === "-1") return "0";
    return cleaned;
};

const formatPlayerName = (player) => {
    const last = cleanValue(player?.last);
    const first = cleanValue(player?.first);

    if (last && first) {
        return `${String(last).toUpperCase()}, ${String(first).toUpperCase()}${cleanValue(player.impnip) ? ` [${cleanValue(player.impnip)}]` : ""}`.trim();
    }

    if (last) {
        return String(last).toUpperCase();
    }

    return "";
};

const SCREEN_EXPORT_COLUMNS = [
    { key: "impnip", header: "Class" },
    {
        header: "Name",
        format: formatPlayerName,
    },
    { key: "genPos", header: "CGP" },
    {
        header: "C#",
        format: (player) => jerseyValue(player.jersey),
    },
    { key: "longSchool", header: "School" },
    { key: "school", header: "CODE" },
    
    { key: "height", header: "HT" },
    { key: "heightV", header: "HT V" },
    { key: "heightDate", header: "HT Date" },
    
    { key: "weight", header: "WT" },
    { key: "weightV", header: "WT V" },
    { key: "weightDate", header: "WT Date" },
    
    { key: "speed40Fastest", header: "SPD" },
    { key: "speed40FVerified", header: "SPD V" },
    { key: "speed40FDate", header: "SPD Date" },
    { key: "speed40FSurface", header: "SPD Sur" },
    {
        header: "P#",
        format: (player) => jerseyValue(player.proJersey),
    },
    
    {
        header: "CFL",
        format: (player) => joinClean(player.cflTeam, player.cflStatus),
    },
    { key: "cflDate", header: "CFL-Date" },
    {
        header: "CFL-DY",
        format: (player) => joinClean(player.cflDraftYear),
    },
    {
        header: "CFL-RND",
        format: (player) => joinClean(player.cflDraftRound),
    },
    { key: "optionYr", header: "FCY" },
    {
        header: "NFL",
        format: (player) => joinClean(player.nflTeam, player.nflStatus),
    },
    { key: "nflDate", header: "NFL-Date" },
    {
        header: "NFL-DY",
        format: (player) => joinClean(player.nflDraftYear),
    },
    {
        header: "NFL-RND",
        format: (player) => joinClean(player.nflDraftRound),
    },
    {
        header: "UFL24",
        format: (player) => joinClean(player.ufl24Team, player.ufl24Status),
    },
    { key: "theEvent", header: "Event" },
    { key: "eventSide", header: "E Side" },
    { key: "eventYear", header: "E Year" },
    { key: "age", header: "Age" },
    { key: "dob", header: "DOB" },
    { key: "masterGrade", header: "HG" },
    {
        header: "RNF",
        format: (player) => joinClean(player.report, player.note, player.pdf),
    },
];

const REPORT_EXPORT_COLUMNS = [
    { key: "impnip", header: "Class" },
    {
        header: "Name",
        format: formatPlayerName,
    },
    { key: "school", header: "School" },
    { key: "genPos", header: "CGP" },
    {
        header: "C#",
        format: (player) => jerseyValue(player.jersey),
    },
    {
        header: "P#",
        format: (player) => jerseyValue(player.proJersey),
    },
    {
        header: "HT",
        format: (player) => joinClean(player.height, player.heightV),
    },
    {
        header: "WT",
        format: (player) => joinClean(player.weight, player.weightV),
    },
    {
        header: "SPD",
        format: (player) => joinClean(player.speed40Fastest, player.speed40FVerified),
    },
    {
        header: "CFL",
        format: (player) => joinClean(player.cflTeam, player.cflStatus),
    },
    { key: "cflDate", header: "CFL-Date" },
    { key: "optionYr", header: "FCY" },
    {
        header: "CFL-DY",
        format: (player) => joinClean(player.cflDraftYear),
    },
    {
        header: "NFL",
        format: (player) => joinClean(player.nflTeam, player.nflStatus),
    },
    { key: "nflDate", header: "NFL-Date" },
    {
        header: "NFL-DY",
        format: (player) => joinClean(player.nflDraftYear),
    },
    {
        header: "UFL24",
        format: (player) => joinClean(player.ufl24Team, player.ufl24Status),
    },
    { key: "theEvent", header: "Event" },
    { key: "calculated", header: "FC" },
    { key: "finalOut", header: "RG" },
    { key: "reportDate", header: "Rep. Date" },
    { key: "reportBy", header: "Rep. By" },
    {
        header: "Exposure",
        format: (player) => cleanValue(player.exposure) || "No Exposure",
    },
    { key: "masterGrade", header: "RG" },
    {
        header: "RNF",
        format: (player) => joinClean(player.report, player.note, player.pdf),
    },
];

const NOTE_EXPORT_COLUMNS = [
    { key: "impnip", header: "Class" },
    {
        header: "Name",
        format: formatPlayerName,
    },
    { key: "school", header: "School" },
    { key: "genPos", header: "CGP" },
    {
        header: "C#",
        format: (player) => jerseyValue(player.jersey),
    },
    {
        header: "P#",
        format: (player) => jerseyValue(player.proJersey),
    },
    {
        header: "HT",
        format: (player) => joinClean(player.height, player.heightV),
    },
    {
        header: "WT",
        format: (player) => joinClean(player.weight, player.weightV),
    },
    {
        header: "SPD",
        format: (player) => joinClean(player.speed40Fastest, player.speed40FVerified),
    },
    {
        header: "CFL",
        format: (player) => joinClean(player.cflTeam, player.cflStatus),
    },
    { key: "cflDate", header: "CFL-Date" },
    { key: "optionYr", header: "FCY" },
    {
        header: "CFL-DY",
        format: (player) => joinClean(player.cflDraftYear),
    },
    {
        header: "NFL",
        format: (player) => joinClean(player.nflTeam, player.nflStatus),
    },
    { key: "nflDate", header: "NFL-Date" },
    {
        header: "NFL-DY",
        format: (player) => joinClean(player.nflDraftYear),
    },
    {
        header: "UFL24",
        format: (player) => joinClean(player.ufl24Team, player.ufl24Status),
    },
    { key: "theEvent", header: "Event" },
    { key: "age", header: "Age" },
    {
        header: "Agt.",
        format: (player) => Number(player.agentNum) > 0 ? "A" : "",
    },
    { key: "contactDate", header: "Note Date" },
    { key: "ByName", header: "Rep. By" },
    {
        header: "Note Title",
        format: (player) => cleanValue(player.sourceData) || "No Note Title Set",
    },
    { key: "masterGrade", header: "HG" },
    {
        header: "RNF",
        format: (player) => joinClean(player.report, player.note, player.pdf),
    },
];

const ExportDropdown = ({ onClose }) => {
    const { playerData, originalPlayerData } = usePlayer();
    const { whichOneLiner } = useDataLayer();
    const isScoutOneLiner = whichOneLiner === "Scout";
    const isNoteOneLiner = whichOneLiner === "Note";
    const exportRowLimit = (() => {
        const [, , security] = String(decodeFBX() || "").split(",");
        return String(security ?? "").trim() === "1"
            ? SECURITY_LEVEL_ONE_EXPORT_ROW_LIMIT
            : EXPORT_ROW_LIMIT;
    })();

    const safeValue = (value) => {
        const cleaned = cleanValue(value);

        if (cleaned === "") return "";
        if (Array.isArray(cleaned)) {
            return cleaned
                .map((item) => cleanValue(item))
                .filter((item) => item !== "")
                .join(", ");
        }
        if (typeof cleaned === "object") return JSON.stringify(cleaned);
        return String(cleaned);
    };

    const escapeCsvValue = (value) => {
        const stringValue = safeValue(value);

        if (
            stringValue.includes(",") ||
            stringValue.includes('"') ||
            stringValue.includes("\n") ||
            stringValue.includes("\r")
        ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
    };

    const getExportValue = (row, column) => {
        if (typeof column.format === "function") {
            return column.format(row);
        }
        return row?.[column.key];
    };

    const getAllKeys = (rows) => {
        const keySet = new Set();

        rows.forEach((row) => {
            if (row && typeof row === "object" && !Array.isArray(row)) {
                Object.keys(row).forEach((key) => keySet.add(key));
            }
        });

        return Array.from(keySet);
    };

    const downloadCsv = (csvLines, exportFileName) => {
        const csvContent = "\uFEFF" + csvLines.join("\r\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${exportFileName}_${getFileTimestamp()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
    };

    const exportRowsCsv = (columns, exportFileName) => {
        if (!Array.isArray(playerData) || playerData.length === 0) {
            alert("No player data to export.");
            return;
        }

        const exportRows = playerData.length > exportRowLimit
            ? [
                ...playerData.slice(0, exportRowLimit),
                { last: EXPORT_LIMIT_LAST_NAME },
            ]
            : playerData;

        const csvLines = [];
        const headers = columns.map((col) => escapeCsvValue(col.header));
        csvLines.push(headers.join(","));

        exportRows.forEach((row) => {
            const line = columns.map((col) =>
                escapeCsvValue(getExportValue(row, col))
            ).join(",");
            csvLines.push(line);
        });

        downloadCsv(csvLines, exportFileName);
        if (onClose) onClose();
    };

    const exportScreenCsv = () => {
        exportRowsCsv(SCREEN_EXPORT_COLUMNS, "screen_export");
    };

    const exportReportCsv = () => {
        exportRowsCsv(REPORT_EXPORT_COLUMNS, "report_export");
    };

    const exportNoteCsv = () => {
        exportRowsCsv(NOTE_EXPORT_COLUMNS, "note_export");
    };

    const exportFullCsv = () => {
        if (!Array.isArray(originalPlayerData) || originalPlayerData.length === 0) {
            alert("No full player data to export.");
            return;
        }

        const headers = getAllKeys(originalPlayerData);

        if (headers.length === 0) {
            alert("No exportable fields found.");
            return;
        }

        const csvLines = [];
        csvLines.push(headers.map((header) => escapeCsvValue(header)).join(","));

        originalPlayerData.forEach((row) => {
            const line = headers.map((header) => escapeCsvValue(row?.[header])).join(",");
            csvLines.push(line);
        });

        downloadCsv(csvLines, "full_data_export");
        if (onClose) onClose();
    };

    return (
        <div
            id="exportBox"
            className="col-26"
            style={{
                position: "fixed",
                top: "60px",
                right: "5px",
                width: BOX_WIDTH,
                maxWidth: BOX_WIDTH,
                backgroundColor: "whitesmoke",
                padding: 20,
                borderRadius: 8,
                boxShadow: "0px 4px 8px rgba(0,0,0,0.3)",
                zIndex: 996,
                border: "2px solid darkgray",
                fontSize: "small",
                boxSizing: "border-box",
            }}
        >
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-bold">Export Options</div>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={onClose}
                    type="button"
                >
                    ✕
                </button>
            </div>

            <div className="d-grid gap-2">
                {isScoutOneLiner ? (
                    <button
                        type="button"
                        className="btn btn-outline-primary text-start"
                        onClick={exportReportCsv}
                    >
                        Report Export
                    </button>
                ) : isNoteOneLiner ? (
                    <button
                        type="button"
                        className="btn btn-outline-primary text-start"
                        onClick={exportNoteCsv}
                    >
                        Note Export
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-outline-primary text-start"
                        onClick={exportScreenCsv}
                    >
                        Screen Export
                    </button>
                )}

                {/* <button
                    type="button"
                    className="btn btn-outline-primary text-start"
                    onClick={exportFullCsv}
                >
                    Full Data Export
                </button> */}
            </div>
        </div>
    );
};

export default ExportDropdown;
